/**
 * ColdStartChoices — the shared BLANK-STATE pattern.
 *
 * When a section/surface has no data yet, its real first job is getting data IN —
 * not configuring downstream machinery (pipelines, automations, reports) that
 * presuppose data that doesn't exist. This block states that job as a small fork
 * of ways to start: a centered hero (icon + title + one line of why), a row of
 * 2–3 "doors" (each a single action — navigate or run an inline handler, with one
 * optionally flagged `recommended`), and a quiet "comes online once you have
 * data" note so the deferred capabilities read as a payoff, not a checklist.
 *
 * Use this when:
 *  • a surface is genuinely empty AND the path forward is "get data in", and
 *  • there's more than one sensible way to do it (e.g. add by hand vs import).
 *
 * Don't use it for:
 *  • an empty VIEW inside a started section (no results, filtered to nothing) —
 *    that's `EmptyState`; or
 *  • a multi-step guided walk where order matters — that's `SetupDriver` /
 *    `SectionColdStart` (the one-at-a-time Play walk).
 *
 * Presentational only: the host owns data/gating (when the surface is empty),
 * navigation targets, and any dialog opened by an `onSelect` door.
 * → docs/design/BLANK-STATE-PATTERN.md
 */

import * as React from "react";
import { RouterLink as Link } from "../router";
import { cn } from "../utils";
import { Card } from "../card";
import { Badge } from "../badge";
import { Icon } from "../icon";
import { ArrowRight } from "../icons";

export type ColdStartBadgeIntent = "info" | "success" | "neutral" | "warning";

export interface ColdStartChoice {
  /** Stable key. */
  key: string;
  /** Icon atom name (e.g. "user-plus", "plug", "sparkles"). */
  icon: string;
  title: string;
  body: string;
  /** Affordance label shown at the foot of the door (defaults to the title). */
  cta?: string;
  /** Navigate here on select. Omit to use `onSelect` (e.g. open a dialog). */
  href?: string;
  /** Inline handler — used when there's no `href` (manual intake, a modal). */
  onSelect?: () => void;
  /** Small category badge (e.g. "Fastest"). */
  badge?: { label: string; intent: ColdStartBadgeIntent };
  /** Emphasize as the suggested path — primary-tinted icon, ring, primary CTA. */
  recommended?: boolean;
}

/** A small text affordance under the doors — a tertiary path or an escape
 *  ("Upload a CSV", "Skip for now"). */
export interface ColdStartLink {
  label: string;
  href?: string;
  onSelect?: () => void;
  /** Optional leading Icon-atom name. */
  icon?: string;
}

export interface ColdStartChoicesProps {
  /** Header icon (Icon-atom name). Defaults to "sparkles". */
  icon?: string;
  title: string;
  description?: string;
  /** The 2–3 doors. */
  choices: ColdStartChoice[];
  /**
   * The capabilities that come online ONCE the surface has data — rendered as a
   * quiet locked note + chips, so deferred features read as a payoff rather than
   * setup the user must do now.
   */
  unlocks?: { note?: string; items?: string[] };
  /** Optional tertiary text links beneath the doors. */
  footerLinks?: ColdStartLink[];
  /** Door columns at ≥sm. Defaults to 2 (or 3 when there are ≥3 choices). */
  columns?: 2 | 3;
  className?: string;
}

const MAXW: Record<2 | 3, string> = { 2: "max-w-2xl", 3: "max-w-3xl" };
const GRID: Record<2 | 3, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
};

function DoorBody({ choice }: { choice: ColdStartChoice }) {
  const rec = choice.recommended;
  return (
    <>
      <div className="flex items-center justify-between">
        <span
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-lg",
            rec ? "bg-primary/15 text-primary" : "bg-secondary/60 text-foreground",
          )}
        >
          <Icon name={choice.icon} size="base" />
        </span>
        {choice.badge ? <Badge variant={choice.badge.intent}>{choice.badge.label}</Badge> : null}
      </div>
      <div className="flex flex-col gap-1">
        <span className="type-body font-medium text-foreground">{choice.title}</span>
        <span className="type-subtext leading-body text-muted-foreground">{choice.body}</span>
      </div>
      <span
        className={cn(
          "mt-1 inline-flex items-center gap-1 type-subtext font-medium",
          rec ? "text-primary" : "text-foreground/70",
        )}
      >
        {choice.cta ?? choice.title}
        <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
    </>
  );
}

function Door({ choice }: { choice: ColdStartChoice }) {
  // All styling lives on <Card>; the child (Link/button) stays BARE so the
  // asChild Slot doesn't double-apply classes (the Card-asChild hydration rule).
  const cardClass = cn(
    "flex flex-col gap-3 p-5 text-left",
    choice.recommended && "border-primary/40 bg-primary/[0.04] ring-1 ring-primary/20",
  );
  if (choice.href) {
    return (
      <Card interactive asChild className={cardClass}>
        <Link href={choice.href}>
          <DoorBody choice={choice} />
        </Link>
      </Card>
    );
  }
  return (
    <Card interactive asChild className={cardClass}>
      <button type="button" onClick={choice.onSelect}>
        <DoorBody choice={choice} />
      </button>
    </Card>
  );
}

function FooterLink({ link }: { link: ColdStartLink }) {
  const inner = (
    <span className="inline-flex items-center gap-1 type-subtext text-muted-foreground transition-colors hover:text-foreground">
      {link.icon ? <Icon name={link.icon} size="xs" /> : null}
      {link.label}
    </span>
  );
  if (link.href) {
    return <Link href={link.href}>{inner}</Link>;
  }
  return (
    <button type="button" onClick={link.onSelect} className="outline-none">
      {inner}
    </button>
  );
}

export function ColdStartChoices({
  icon = "sparkles",
  title,
  description,
  choices,
  unlocks,
  footerLinks,
  columns,
  className,
}: ColdStartChoicesProps) {
  const cols: 2 | 3 = columns ?? (choices.length >= 3 ? 3 : 2);
  return (
    <div className={cn("mx-auto flex w-full flex-col gap-6", MAXW[cols], className)}>
      {/* hero — icon + title + one line of why */}
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Icon name={icon} className="text-primary" />
        </div>
        <h2 className="type-title font-medium tracking-tight text-foreground">{title}</h2>
        {description ? (
          <p className="max-w-md type-body leading-body text-muted-foreground">{description}</p>
        ) : null}
      </div>

      {/* the doors */}
      <div className={cn("grid gap-3", GRID[cols])}>
        {choices.map((c) => (
          <Door key={c.key} choice={c} />
        ))}
      </div>

      {/* tertiary links */}
      {footerLinks && footerLinks.length > 0 ? (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {footerLinks.map((l) => (
            <FooterLink key={l.label} link={l} />
          ))}
        </div>
      ) : null}

      {/* what comes online once there's data — payoff, not a checklist */}
      {unlocks && (unlocks.note || (unlocks.items && unlocks.items.length > 0)) ? (
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 type-subtext text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Icon name="lock" size="xs" className="opacity-70" />
            {unlocks.note ?? "Comes online once you have data"}
          </span>
          {unlocks.items && unlocks.items.length > 0 ? (
            <span className="flex flex-wrap items-center gap-1">
              {unlocks.items.map((u) => (
                <span
                  key={u}
                  className="rounded-full border border-border/60 px-2 py-0.5 type-subtext text-foreground/60"
                >
                  {u}
                </span>
              ))}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
